// src/components/common/MobileShareButton.jsx
import React, { useState, useEffect, useRef } from "react";
import { Share2, Download, Link2, X } from "lucide-react";

/**
 * MobileShareButton - 모바일 공유 버튼 컴포넌트
 * 
 * @param {string} title - 공유할 문서 제목
 * @param {string} contentId - PDF로 변환할 요소의 ID
 * @param {string} className - 추가 CSS 클래스
 */
export default function MobileShareButton({
  title = "문서",
  contentId,
  className = "",
}) {
  const [showMenu, setShowMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const menuRef = useRef(null);

  // 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  // html2pdf.js 동적 로드
  useEffect(() => {
    if (!window.html2pdf) {
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
      script.async = true;
      document.body.appendChild(script);
      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }
  }, []);

  // PDF 다운로드
  const handlePdfDownload = async () => {
    if (!contentId) {
      alert("PDF 변환할 콘텐츠가 없습니다.");
      return;
    }

    const element = document.getElementById(contentId);
    if (!element) {
      alert("콘텐츠를 찾을 수 없습니다.");
      return;
    }

    if (!window.html2pdf) {
      alert("PDF 라이브러리 로딩 중입니다. 잠시 후 다시 시도해주세요.");
      return;
    }

    setLoading(true);
    try {
      const options = {
        margin: 10,
        filename: `${title}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: {
          dpi: 200,
          letterRendering: true,
          useCORS: true,
          scale: 2
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
        },
      };

      await window.html2pdf().set(options).from(element).save();
    } catch (err) {
      console.error("PDF 생성 실패:", err);
      alert("PDF 생성에 실패했습니다.");
    } finally {
      setLoading(false);
      setShowMenu(false);
    }
  };

  // 네이티브 공유 (Web Share API)
  const handleNativeShare = async () => {
    const shareData = {
      title: title,
      text: `${title} - PMIS`,
      url: window.location.href,
    };

    try {
      if (navigator.share && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        // Fallback: 클립보드 복사
        await handleCopyLink();
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("공유 실패:", err);
        // Fallback: 클립보드 복사
        await handleCopyLink();
      }
    }
    setShowMenu(false);
  };

  // 링크 복사
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      alert("링크가 복사되었습니다.");
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = window.location.href;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      alert("링크가 복사되었습니다.");
    }
    setShowMenu(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* 공유 버튼 */}
      <button
        onClick={() => setShowMenu(!showMenu)}
        className={`btn-basic flex items-center gap-1.5 text-xs px-3 py-1.5 ${className}`}
        disabled={loading}
      >
        <Share2 className="w-3.5 h-3.5" />
        <span>공유</span>
      </button>

      {/* 드롭다운 메뉴 */}
      {showMenu && (
        <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg border border-gray-200 shadow-lg z-50 overflow-hidden animate-fadeIn">
          {/* PDF 다운로드 */}
          {contentId && (
            <button
              onClick={handlePdfDownload}
              disabled={loading}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <Download className="w-4 h-4 text-gray-500" />
              <span>{loading ? "생성 중..." : "PDF 다운로드"}</span>
            </button>
          )}

          {/* 공유하기 (네이티브) */}
          <button
            onClick={handleNativeShare}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
          >
            <Share2 className="w-4 h-4 text-gray-500" />
            <span>공유하기</span>
          </button>

          {/* 링크 복사 */}
          <button
            onClick={handleCopyLink}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors border-t border-gray-100"
          >
            <Link2 className="w-4 h-4 text-gray-500" />
            <span>링크 복사</span>
          </button>
        </div>
      )}
    </div>
  );
}
